<template>
  <section id="config" class="config">
    <div class="config-editor">
      <section v-show="!single" class="editor-header">
        <px-input ref="configName"
                  :id="`config-${config.order}-name`"
                  v-model="configName"
                  placeholder="Add Config Name"
                  class="config-name compact">
          <span>Configuration Name:</span>
        </px-input>
        <div class="remove-config-wrap">
          <button class="remove-config secondary compact" @click="$emit('remove', config.id)">
            <span>Remove this configuration</span>
          </button>
        </div>
      </section>
      <section id="channels" class="form-section">
        <subject-with-help>
          <h3>Channels</h3>
          <template v-slot:help>
            In the <b>Channels</b> you define a <b>number of players</b>,
            theirs <b>sources</b> & <b>outputs</b> needed for presentation of the work in the current configuration.
          </template>
        </subject-with-help>
        <tabs ref="channels"
              v-model="channelsTabs"
              class="channel-tabs"
              :class="{long:channelsTabs.length > 9}"
              editable
              @add="addChannel"
              @switchTab="scrollToChannelID">
          <template v-slot:tab-content>
            <observable-scroll-box id="channels-container"
                                   class="scene"
                                   :observe-threshold="thresholdList(10)"
                                   :sort-func="channelInViewSortFunc"
                                   @in-view="selectInViewChannel">
              <channel-editor
                v-for="(channel, i) in channels"
                :key="channel.id"
                :id="channelBoxID(channel.id)"
                :value="channel"
                :channelId="channel.id"
                :index="i"
                :removable="channels.length > 1"
                :class="{active: channel.id === activeChannelID}"
                @input="updateChannel(i, $event)"
                @newTitleCandidate="$emit('newTitleCandidate', $event)"
                @remove="removeChannel"/>
            </observable-scroll-box>
          </template>
        </tabs>
      </section>
      <section>
        <h4>Video Preview</h4>
        <div id="preview" class="form-section">
          <div class="field">
            <video-attachment-editor v-model="config.preview"/>
          </div>
        </div>
        <h4>Images</h4>
        <div class="row">
          <div class="field">
            <image-attachment-editor/>
          </div>
        </div>
      </section>
    </div>
  </section>
</template>

<script>
import { PxAWConfiguration, PxAWChannel } from '@pxflux/px-notation'
import ObservableScrollBox from '../../elements/UI/ObservableScrollBox'
import PxInput from '../../elements/UI/inputs/PxInput'
import ImageAttachmentEditor from './ImageAttachmentEditor'
import VideoAttachmentEditor from './VideoAttachmentEditor'
import ChannelEditor from './channel-editor/ChannelEditor'
import SubjectWithHelp from '../../elements/UI/SubjectWithHelp'
import Tabs from '../../elements/UI/Tabs'

export default {
  name: 'ArtworkConfigEditor',
  components: {
    ObservableScrollBox,
    PxInput,
    ImageAttachmentEditor,
    VideoAttachmentEditor,
    ChannelEditor,
    SubjectWithHelp,
    Tabs
  },
  props: {
    value: Object,
    single: Boolean,
    index: { type: [Number, String], required: true }
  },
  data () {
    let config = this.value ? this.value : PxAWConfiguration.preset()
    return {
      config: config,
      configIndex: 0,
      scrolling: false,
      renameConfigOpen: false,
      activeChannelID: 0,
      stopAutoSelect: false
    }
  },
  computed: {
    configuration: {
      get () { return this.value },
      set (newValue) { this.$emit('input', newValue) }
    },
    configName: {
      get () { return this.config.title },
      set (newValue) {
        this.config.title = newValue
        this.update()
      }
    },
    channelsTabs: {
      get () {
        return this.channels.map((ch, i) => ({ id: ch.id, label: i + 1 }))
      },
      set (value) {
        value.forEach((item, i) => {
          const index = this.config.channels.findIndex(s => s.id === item.id)
          if (index >= 0) {
            this.$set(this.config.channels[index], 'order', i)
          }
        })
        this.update()
      }
    },
    channels: {
      get () {
        return [...this.configuration.channels].sort((a, b) => a.order - b.order)
      },
      set (value) {
        this.$set(this.configuration, 'channels', value.map((ch, i) => {
          ch.order = i
          return ch
        }))
        this.update()
      }
    }
  },
  methods: {
    addChannel () {
      const ch = PxAWChannel.preset()
      ch.order = this.config.channels.length
      this.config.channels.push(ch)
      this.selectChannelByIndex(this.config.channels[this.config.channels.length - 1].id)
    },

    updateChannel (index, event) {
      this.config.channels[index] = event
      this.update()
    },

    removeChannel (index) {
      this.config.channels.splice(index, 1)
      this.config.channels.forEach((ch, i) => { ch.order = i })
      this.selectChannelByIndex(index - 1)
      this.update()
    },

    async selectChannelByIndex (index) {
      if (index < 0) {
        index = 0
      } else if (index >= this.config.channels.length) {
        index = this.config.channels.length - 1
      }
      await this.$nextTick()
      this.$refs.channels.switchTab(index)
    },

    scrollToChannelID (id) {
      this.activeChannelID = id
      this.stopAutoSelect = true

      this.$scrollTo(`#${this.channelBoxID(id)}`, {
        container: '#channels-container',
        x: true,
        y: false,
        onDone: () => { this.stopAutoSelect = false }
      })
    },

    selectInViewChannel (inViewList) {
      if (this.stopAutoSelect) return
      if (!inViewList.length) return
      const activeItem = inViewList[0]
      inViewList.forEach(item => {
        if (item !== activeItem && item.el.classList.contains('active')) {
          item.el.classList.remove('active')
        } else if (item === activeItem && !item.el.classList.contains('active')) {
          item.el.classList.add('active')
        }
      })
      this.$refs.channels.passiveSwitchTab(this.channelIdFromBoxId(activeItem.el.id))
    },

    channelInViewSortFunc (a, b) {
      const tolerance = 0.1
      const ratioDelta = a.intersectionRatio - b.intersectionRatio
      if (ratioDelta > tolerance) return -1
      if (ratioDelta < tolerance) return 1
      return a.intersectionRect.x - b.intersectionRect.x
    },

    focusName () {
      if (!this.$refs.configName) return
      this.$nextTick(() => {
        this.$refs.configName.focus()
      })
    },

    update () {
      this.$emit('input', this.config)
    },

    channelBoxID (id) { return 'ch' + id },

    channelIdFromBoxId (boxId) { return boxId.substring(2) },

    thresholdList (numSteps) {
      let thresholds = []
      if (!numSteps) numSteps = 1
      for (let i = 1.0; i <= numSteps; i++) {
        let ratio = i / numSteps
        thresholds.push(ratio)
      }
      thresholds.push(0)
      return thresholds
    }
  },
  watch: {
    value: function () {
      if (this.value) {
        this.config = this.value
      } else {
        this.config = PxAWConfiguration.preset()
      }
    }
  }
}
</script>

<style lang="scss">
  @import "../../../scss/vars";
  .config {
    section {
      padding: 0 $basic-padding;
    }
    .editor-header {
      display: flex;
      align-items: flex-end;

      .config-name {
        flex-grow: 1;
        overflow: hidden;

        label {
          flex-flow: column;
          align-items: flex-start;
          span {
            padding: 0 0 0 $small-padding;
            margin-bottom: $small-padding;
          }
        }

        input {
          max-width: 100%;
        }
      }

      .remove-config-wrap {
        margin-left: auto;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        height: $basic-size;
        width: min-content;
        padding: 0;

        button {
          color: $px-color-grey;

          &:focus, &:hover {
            color: $error-color;
          }
        }
      }
    }
    .channel-tabs {
      > .tab-bar-scroll-box {
        padding-top: calc(#{$basic-padding} + 2px);

        .tab-bar .tab {
          min-width: unset;
          width: $basic-size;
          height: $basic-size;
          padding: 0;
          color: $link-color;
          .label {
            font-size: $h1-font-size;
            line-height: 1;
            padding: 0;
            overflow: visible;
            text-overflow: unset;
          }

          &.active {
            border-radius: 50%;
            background: #fff;

            &:before{
              content: 'player';
              font-size: $font-size-micro;
              position: absolute;
              bottom: calc(100% + 4px);
              color: $px-color-grey;
              letter-spacing: 0.03em;
            }
          }
        }
      }
      &.long {
        > .tab-bar-scroll-box .tab .label {
          font-size: $h1-font-size * 0.8;
        }
      }
    }
    #channels-container {
      display: flex;
    }
  }
</style>
